import React from 'react';
import { Trash2, Plus, X, Upload, Edit2 } from 'lucide-react';

const RealizationsTab = ({
  realizations, showForm, setShowForm, editingReal, setEditingReal,
  realForm, setRealForm, uploadingId,
  onCreateRealization, onUpdateRealization, onDeleteRealization,
  onUploadPhoto, onDeletePhoto, onStartEdit,
}) => {
  const defaultForm = { title: '', category: 'Mariage', location: '', guests: 0, description: '' };

  const handleAdd = () => {
    setShowForm(true);
    setEditingReal(null);
    setRealForm(defaultForm);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingReal(null);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-500">{realizations.length} réalisations</span>
        <button onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors" data-testid="add-realization-btn">
          <Plus className="w-4 h-4" /> Ajouter une réalisation
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showForm || editingReal) && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6" data-testid="realization-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{editingReal ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</h3>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
              <input type="text" value={realForm.title} onChange={(e) => setRealForm(f => ({ ...f, title: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="real-title-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select value={realForm.category} onChange={(e) => setRealForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="real-category-select">
                {['Mariage', 'Entreprise', 'Reception', 'Anniversaire', 'Festival', 'Religieux'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
              <input type="text" value={realForm.location} onChange={(e) => setRealForm(f => ({ ...f, location: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="real-location-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nb invités</label>
              <input type="number" value={realForm.guests} onChange={(e) => setRealForm(f => ({ ...f, guests: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" data-testid="real-guests-input" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={realForm.description} onChange={(e) => setRealForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none" rows={2} data-testid="real-description-input" />
            </div>
          </div>
          <button onClick={editingReal ? onUpdateRealization : onCreateRealization}
            className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium" data-testid="real-save-btn">
            {editingReal ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      )}

      {/* Realizations List */}
      <div className="space-y-4" data-testid="admin-realizations-list">
        {realizations.map((real) => (
          <div key={real.id} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`admin-real-${real.id}`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-900">{real.title}</h3>
                <p className="text-sm text-gray-500">{real.category} — {real.location}{real.guests > 0 ? ` — ${real.guests} pers.` : ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onStartEdit(real)} className="p-2 text-gray-400 hover:text-orange-500" data-testid={`edit-real-${real.id}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => onDeleteRealization(real.id)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`delete-real-${real.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="flex flex-wrap gap-3 items-center">
                {(real.images || []).map((img) => (
                  <div key={img} className="relative group w-24 h-20 rounded-lg overflow-hidden border border-gray-200">
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                    <button onClick={() => onDeletePhoto(real.id, img)}
                      className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-24 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
                  data-testid={`upload-photo-${real.id}`}>
                  {uploadingId === real.id ? (
                    <div className="w-5 h-5 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">Photo</span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => e.target.files.length > 0 && onUploadPhoto(real.id, e.target.files)} />
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-2">{(real.images || []).length} photo(s)</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default RealizationsTab;
